import { add, subMilliseconds } from 'date-fns';
import { DateTime, Duration } from 'luxon';

import { TIME_INTERVAL_PRESETS } from '@/pages/Insights/Insights.constants';
import { TypeDuration, TypeTimeInterval } from '@/pages/Insights/Insights.types';

import { bucketEndOfInterval, bucketStartOfInterval } from '../Context/UrlContext';

const additionMap = {
  secondly: 'seconds',
  hourly: 'hours',
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
};

export const intervalEndFromDate = (dateFrom: Date, interval: TypeTimeInterval) => {
  return subMilliseconds(add(dateFrom, { [additionMap[interval]]: 1 }), 1);
};

export const timeRangeFromNow = (interval: TypeTimeInterval, duration: TypeDuration) => {
  const now = DateTime.now();

  const referenceTime = now.endOf('hour').plus(1);

  const timeInterval =
    TIME_INTERVAL_PRESETS[interval].find((range) => {
      return range.value === duration;
    }) || TIME_INTERVAL_PRESETS[interval][0];

  const dateFrom = referenceTime.minus(Duration.fromISO(duration as string).toObject());
  const dateTo = referenceTime;
  const { text } = timeInterval;

  return {
    dateFrom,
    dateFromInMs: bucketStartOfInterval(interval, dateFrom.toUTC()).toMillis(),
    dateTo,
    dateToInMs: bucketEndOfInterval(interval, dateTo.toUTC()).toMillis(),
    text,
  };
};

export const timeRangeBetweenDates = (dateFrom: DateTime, dateTo: DateTime, interval: TypeTimeInterval) => {
  let dateFormat = interval === 'hourly' ? 'MMM d, HH:mm' : 'MMM d';
  if (interval === 'monthly') dateFormat = 'LLL';

  const isSameDay =
    dateFrom.year === dateTo.year &&
    dateFrom.month === dateTo.month &&
    dateFrom.day === dateTo.day &&
    interval !== 'hourly';

  const text = isSameDay
    ? dateFrom.toFormat(dateFormat)
    : `${dateFrom.toFormat(dateFormat)} - ${dateTo.toFormat(dateFormat)}`;

  return {
    dateFrom,
    dateTo,
    text,
  };
};

export const timeRange = (
  interval: TypeTimeInterval,
  duration: TypeDuration,
  dateFromAsUnixSeconds?: number,
  dateToAsUnixSeconds?: number,
) => {
  const dateFrom = dateFromAsUnixSeconds ? DateTime.fromSeconds(dateFromAsUnixSeconds).toUTC() : undefined;
  const dateTo = dateToAsUnixSeconds ? DateTime.fromSeconds(dateToAsUnixSeconds).toUTC() : undefined;

  if (duration === 'FIXED' && dateFrom && dateTo) {
    return timeRangeBetweenDates(dateFrom, dateTo, interval);
  }

  return timeRangeFromNow(interval, duration);
};

export const formatSecondsToString = (allSeconds: number) => {
  if (allSeconds === 0) {
    return '0s';
  }

  const days = Math.floor(allSeconds / 86400);
  const hours = Math.floor((allSeconds - days * 86400) / 3600);
  const minutes = Math.floor((allSeconds - days * 86400 - hours * 3600) / 60);
  const seconds = Math.floor(allSeconds - days * 86400 - hours * 3600 - minutes * 60);

  if (allSeconds < 0.1) {
    return `${(allSeconds * 1000).toFixed(2)}ms`;
  }

  if (allSeconds < 60) {
    return `${allSeconds.toFixed(2)}s`;
  }

  if (allSeconds < 3600) {
    return `${minutes}m ${seconds}s`;
  }

  if (days === 0 && hours !== 0) {
    return `${hours}h ${minutes}m`;
  }

  if (days === 0 && hours === 0) {
    return `${minutes}m`;
  }

  return `${days}d ${hours}h ${minutes}m`;
};

export const timeRangeAroundTimestamp = (isoString: string, interval: TypeTimeInterval, duration: TypeDuration) => {
  const dateInMs = bucketStartOfInterval(interval, DateTime.fromISO(isoString)).toMillis();

  const halfOfDuration = Duration.fromISO(duration as string).toMillis() / 2;

  return {
    dateFrom: Math.round((dateInMs - halfOfDuration) / 1000),
    dateTo: Math.round((dateInMs + halfOfDuration) / 1000),
  };
};
